import { useCallback, useMemo, useState } from 'react';

import { useCurrentUserQuery } from '../api';
import { useUpdateUserFeatureTogglesMutation } from '../api';
import { userFeatureToggles } from '../model';

type UserFeatureToggleName = (typeof userFeatureToggles)[keyof typeof userFeatureToggles];

export function useUserFeatureToggle(toggleName: UserFeatureToggleName, defaultValue = false) {
  const [localOverride, setLocalOverride] = useState<boolean | null>(null);
  const updateUserFeatureTogglesMutation = useUpdateUserFeatureTogglesMutation();
  const { data: currentUser } = useCurrentUserQuery();

  const profileEnabled = useMemo(() => {
    return currentUser?.features?.find((f) => f.name === toggleName)?.enabled ?? defaultValue;
  }, [currentUser, toggleName, defaultValue]);

  const persist = useCallback(() => {
    if (localOverride === null || localOverride === profileEnabled) return;
    updateUserFeatureTogglesMutation.mutate([{ name: toggleName, enabled: localOverride }]);
  }, [localOverride, profileEnabled, toggleName, updateUserFeatureTogglesMutation]);

  const checkAndPersist = useCallback(
    (enabled: boolean) => {
      setLocalOverride(enabled);
      if (enabled === profileEnabled) return;
      updateUserFeatureTogglesMutation.mutate([{ name: toggleName, enabled }]);
    },
    [profileEnabled, toggleName, updateUserFeatureTogglesMutation],
  );

  const checked = localOverride ?? profileEnabled;

  return { checked, setChecked: setLocalOverride, persist, checkAndPersist };
}
